import React from 'react';
import { 
  Globe, 
  Facebook, 
  CalendarClock, 
  MessageCircleCode, 
  Webhook, 
  BarChart3,
  Sparkles,
  Zap
} from 'lucide-react';

const features = [
  {
    icon: <Globe className="h-6 w-6" />,
    title: "AI-Powered Website Builder",
    description: "Custom property sites with zero design work needed.",
    gradient: "from-blue-500 to-cyan-500"
  },
  {
    icon: <Facebook className="h-6 w-6" />,
    title: "Facebook & Google Lead Ads Auto-Gen",
    description: "Create & optimize high-converting ad campaigns instantly.",
    gradient: "from-purple-500 to-pink-500"
  },
  {
    icon: <CalendarClock className="h-6 w-6" />,
    title: "SEO Blog Posts Scheduler",
    description: "Automatic content creation tailored to local Dubai market.",
    gradient: "from-green-500 to-emerald-500"
  },
  {
    icon: <MessageCircleCode className="h-6 w-6" />,
    title: "WhatsApp Chatbot for Lead Nurture",
    description: "24/7 automated qualification and follow-up for every lead.",
    gradient: "from-orange-500 to-red-500"
  },
  {
    icon: <Webhook className="h-6 w-6" />,
    title: "CRM Webhook Integration",
    description: "Seamless connection with your existing tools and workflows.",
    gradient: "from-indigo-500 to-purple-500"
  },
  {
    icon: <BarChart3 className="h-6 w-6" />,
    title: "Performance Dashboard",
    description: "Real-time analytics on campaign and lead performance.",
    gradient: "from-cyan-500 to-blue-500"
  }
];

const FeaturesGrid = () => {
  return (
    <section id="features" className="py-20 lg:py-28 bg-gray-50 dark:bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-2" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Powerful Features</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Everything You Need to{' '}
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Succeed
            </span>
          </h2>
          <p className="text-lg lg:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Powerful features designed specifically for Dubai real estate professionals to dominate their market
          </p>
        </div>
        
        {/* Features grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group bg-white dark:bg-slate-700 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 p-8 border border-gray-100 dark:border-slate-600 hover:-translate-y-2"
            >
              {/* Icon container */}
              <div className={`p-4 bg-gradient-to-br ${feature.gradient} rounded-xl text-white inline-block mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                {feature.icon}
              </div>
              
              {/* Content */}
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white leading-tight">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {feature.description}
              </p>
              
              {/* Hover effect indicator */}
              <div className={`mt-6 h-1 w-0 bg-gradient-to-r ${feature.gradient} rounded-full transition-all duration-300 group-hover:w-full`}></div>
            </div>
          ))}
        </div>
        
        {/* Bottom highlight section */}
        <div className="mt-16 lg:mt-20">
          <div className="bg-white dark:bg-slate-700 rounded-2xl p-8 lg:p-12 shadow-xl border border-gray-100 dark:border-slate-600">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-full mb-4">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">All-in-One Platform</span>
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Stop Juggling Multiple Tools
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  Replace 5+ different marketing tools with one comprehensive platform. Save time, reduce costs, and get better results.
                </p>
                <a 
                  href="#demo" 
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  See All Features
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
              
              {/* Stats/benefits */}
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-slate-800 dark:to-slate-600 rounded-xl">
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">5+</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Tools Replaced</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-slate-800 dark:to-slate-600 rounded-xl">
                  <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">80%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Time Saved</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-orange-50 to-red-50 dark:from-slate-800 dark:to-slate-600 rounded-xl">
                  <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">24/7</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">AI Support</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-slate-800 dark:to-slate-600 rounded-xl">
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">3x</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">More Leads</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;